<template>
  <img
    :src="getImageSrc()"
    :srcset="getSrcSet()"
    :sizes="getSizes()"
    :loading="priority ? 'eager' : 'lazy'"
    decoding="async"
    :alt="alt"
    :class="className"
  />
</template>
  
<script setup lang="ts">
import { useOptimizedImage } from '../composables/useOptimizedImage'

interface Props {
  src?: string
  alt: string
  className?: string
  priority?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  className: '',
  priority: false
})

const widths = [640, 750, 828, 1080, 1200, 1920]

const { getOptimizedPath } = useOptimizedImage()

const isCloudinaryImage = (src: string) => {
  return src?.includes('res.cloudinary.com');
}

const getCloudinaryUrl = (src: string, width: number) => {
  // Extract base URL and transformation parameters
  const [baseUrl, ...params] = src.split('/upload/');
  if (!baseUrl) return src;
  
  // Add width transformation
  return `${baseUrl}/upload/w_${width},c_limit/${params.join('/upload/')}`;
}

const getImageSrc = () => {
  if (!props.src) return '';
  if (isCloudinaryImage(props.src)) {
    return props.src;
  }
  return getOptimizedPath(props.src, 828)?.webp || props.src;
}

const getSrcSet = () => {
  if (!props.src) return '';
  
  if (isCloudinaryImage(props.src)) {
    return widths
      .map(width => `${getCloudinaryUrl(props.src || '', width)} ${width}w`)
      .join(', ');
  }
  
  // For local images, generate WebP srcset
  return widths
    .map(width => {
      const path = getOptimizedPath(props.src || '', width)?.webp
      return `${path} ${width}w`
    })
    .join(', ');
}

const getSizes = () => {
  return widths
    .map((w, i) => 
      i === widths.length - 1 
        ? `${w}px`
        : `(max-width: ${w}px) ${w}px`
    )
    .join(', ');
}
</script>